import { SubtreeController } from "subtree.js/core";
import type { AppDeps } from "../../app-deps";
import { loadFeePolicy } from "../../core/ops/fee-ops";
import { connectStripe, refreshProvider, stripeDashboardLink } from "../../core/ops/provider-ops";
import { errorText } from "../../core/primitives/errors";
import { deferPayments, markStripeReturnsToWizard, sellingPath } from "../../core/primitives/onboarding";
import { OnboardingPaymentsActions, OnboardingPaymentsState } from "./onboarding-payments-model";

export type OnboardingPaymentsDeps = Pick<
  AppDeps,
  "client" | "authHandler" | "storeRepository" | "providersRepository"
>;

export interface OnboardingPaymentsRouting {
  /** Sends the browser to the provider; the page is left behind. */
  openExternal(url: string): void;
  toDashboard(storeId: string | null): void;
}

/**
 * Step 4: Stripe. The providers come from the repository the shell keeps fresh
 * (a slow poll, and after every connect, refresh or disconnect), so the screen
 * follows a review Stripe finishes without telling this app.
 */
export class OnboardingPaymentsController extends SubtreeController implements OnboardingPaymentsActions {
  readonly state = new OnboardingPaymentsState();
  #knownFor: string | null = null;

  constructor(
    readonly deps: OnboardingPaymentsDeps,
    readonly routing: OnboardingPaymentsRouting,
  ) {
    super();
    this.subtree.put(OnboardingPaymentsState, this.state);
    this.subtree.put(OnboardingPaymentsActions, this);
    this.sync(() => {
      const storeId = deps.storeRepository.shopId;
      const repo = deps.providersRepository;
      this.state.storeId.value = storeId;
      this.state.sellsAtShows.value = storeId !== null && sellingPath(storeId) === "shows";
      if (storeId && repo.isLoadedFor(storeId)) this.#knownFor = storeId;
      const known = storeId !== null && this.#knownFor === storeId;
      this.state.providersLoading.value = !known;
      this.state.connection.value = known ? repo.find("stripe") : null;
    }, [deps.storeRepository, deps.providersRepository]);
    void this.#loadFees();
  }

  connect = async () => {
    const storeId = this.state.storeId.value;
    if (!storeId || this.state.busy.value) return;
    this.state.error.value = null;
    this.state.busy.value = true;
    const r = await connectStripe({ ...this.deps, storeId });
    if (this.isDisposed) return;
    if (!r.ok) {
      this.state.error.value = errorText(r.errors);
      this.state.busy.value = false;
      return;
    }
    // Stripe returns through the backend, which redirects to store settings.
    // Leave a breadcrumb so the gate brings the user back here instead.
    markStripeReturnsToWizard();
    this.routing.openExternal(r.value.url);
  };

  recheck = async () => {
    const storeId = this.state.storeId.value;
    if (!storeId || this.state.busy.value) return;
    this.state.error.value = null;
    this.state.busy.value = true;
    const r = await refreshProvider({ ...this.deps, storeId, providerKey: "stripe" });
    if (this.isDisposed) return;
    this.state.busy.value = false;
    if (!r.ok) this.state.error.value = errorText(r.errors);
  };

  openDashboard = async () => {
    const storeId = this.state.storeId.value;
    if (!storeId || this.state.busy.value) return;
    this.state.error.value = null;
    this.state.busy.value = true;
    const r = await stripeDashboardLink({ ...this.deps, storeId });
    if (this.isDisposed) return;
    this.state.busy.value = false;
    if (!r.ok) {
      this.state.error.value = errorText(r.errors);
      return;
    }
    this.routing.openExternal(r.value.url);
  };

  finish = () => {
    const storeId = this.state.storeId.value;
    // Also on the connected path, where it is a no-op the resolver never reads:
    // an ACTIVE connection already ends the wizard on its own.
    if (storeId) deferPayments(storeId);
    this.routing.toDashboard(storeId);
  };

  async #loadFees(): Promise<void> {
    const r = await loadFeePolicy(this.deps);
    if (this.isDisposed || !r.ok) return;
    this.state.fees.value = r.value;
  }
}
